// Lib
import { useMemo } from 'react';
import * as Immutable from 'immutable';

// Utils
import { getDrawingData, getHasDrawing } from '../../../../../../../common/elements/utils/elementPropertyUtils';
import { asObject } from '../../../../../../../common/utils/immutableHelper';
import { getDrawingTransformResult } from '../../../utils/imageDrawingTransformUtils';
import { getImageModalEditorDrawingViewBox } from '../../../content/utils/imageModalEditorDrawingUtils';

// Types
import type { ImMNElement, MNDrawingContent } from '../../../../../../../common/elements/elementModelTypes';
import type { ImageModalV2SessionTransform } from './useImageModalV2EditorTransformSession';

type UseImageModalV2DrawingPreviewOptions = {
    // Drawing presence/visibility follows the displayed (possibly frozen) element, not the live prop, so
    // the overlay stays put while a save is in flight.
    displayElement: ImMNElement;
    shouldUseOriginalEditorSource: boolean;
    drawingSessionTransform: ImageModalV2SessionTransform;
    hasSessionDrawingTransform: boolean;
};

/**
 * Derives the drawing overlay the V2 editor renders: the display element with the session's rotate/flip
 * path-baked into its SVG (so the drawing tracks the transform live) and the view box that maps it onto
 * the active crop source.
 */
export const useImageModalV2DrawingPreview = ({
    displayElement,
    shouldUseOriginalEditorSource,
    drawingSessionTransform,
    hasSessionDrawingTransform,
}: UseImageModalV2DrawingPreviewOptions) => {
    const hasDrawing = getHasDrawing(displayElement);

    const drawingPreviewElement = useMemo(() => {
        if (!hasDrawing || !hasSessionDrawingTransform) return displayElement;

        const drawing = asObject<MNDrawingContent>(getDrawingData(displayElement));
        if (!drawing?.svg) return displayElement;

        const result = getDrawingTransformResult(drawing, drawingSessionTransform);
        if (result.status !== 'transformed') return displayElement;

        // Immutable's setIn typing widens to a plain Map; the record shape is unchanged.
        return displayElement.setIn(['content', 'drawing'], Immutable.fromJS(result.drawing)) as unknown as ImMNElement;
    }, [displayElement, drawingSessionTransform, hasDrawing, hasSessionDrawingTransform]);

    const drawingViewBox = getImageModalEditorDrawingViewBox(
        displayElement,
        shouldUseOriginalEditorSource,
        drawingSessionTransform,
    );

    return { hasDrawing, drawingPreviewElement, drawingViewBox };
};
